/**
 * Bulk-promo client utilities.
 *
 * The storefront previews the operator's "Number of Items" auto-apply
 * promo codes in real time on every product surface so customers see
 * the discount they'd unlock by buying more — Shop cards advertise the
 * lowest-threshold teaser, Product Detail and the cart drawer show the
 * exact applicable discount for the current quantity.
 *
 * Live polling matches the rest of the app (15s + on tab-visibility).
 * Rules are tiny (~4 entries today, capped at ~200 server-side) so
 * re-fetching is essentially free.
 */
import { useEffect, useState } from "react";
import { getJson } from "./http";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 15_000;

/**
 * Hook returning the live bulk-promo rules sorted ascending by
 * threshold. Returns `null` until the first fetch resolves so callers
 * can avoid flashing "no discount" while the rules load.
 */
// Module-level shared store: ONE poller feeds every subscribed
// component, no matter how many product cards are on screen. The
// previous per-hook polling meant 10+ identical requests every 15s on
// the shop grid, and every response re-rendered every card (fresh
// array identity) — a measurable Lighthouse TTI/LCP penalty.
let _rules = null;
let _rulesJson = null;
const _subs = new Set();
let _pollerStarted = false;

const _fetchRules = async () => {
  try {
    const data = await getJson(`${API}/promo-codes/bulk`);
    const next = data?.rules || [];
    const nextJson = JSON.stringify(next);
    if (nextJson === _rulesJson) return; // identity-stable: no re-renders
    _rules = next;
    _rulesJson = nextJson;
  } catch {
    // Silently fall back to "no rules" — a failed preview must
    // never block adding to cart. The real discount is still
    // computed server-side at /checkout/quote so it can't be
    // bypassed.
    if (_rules !== null) return;
    _rules = [];
    _rulesJson = "[]";
  }
  _subs.forEach((fn) => fn(_rules));
};

const _startPoller = () => {
  if (_pollerStarted) return;
  _pollerStarted = true;
  _fetchRules();
  setInterval(_fetchRules, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") _fetchRules();
  });
};

export function useBulkPromos() {
  const [rules, setRules] = useState(_rules);

  useEffect(() => {
    _subs.add(setRules);
    _startPoller();
    if (_rules !== null) setRules(_rules);
    return () => {
      _subs.delete(setRules);
    };
  }, []);

  return rules;
}

/**
 * Given a unit price, quantity, and the rules list, return the
 * applicable rule + dollar discount for that single product group.
 * Mirrors the server-side `resolve_auto_promos` math (highest
 * threshold ≤ qty wins; no compounding).
 *
 * @returns {{rule: Object, amount: number} | null}
 */
export function computeBulkDiscount(unitPrice, qty, rules) {
  if (!rules || rules.length === 0) return null;
  const q = Number(qty || 0);
  const u = Number(unitPrice || 0);
  if (q <= 0 || u <= 0) return null;
  // Rules come in ascending order from the API; walk backwards to find
  // the highest applicable threshold.
  let chosen = null;
  for (let i = rules.length - 1; i >= 0; i -= 1) {
    if (q >= Number(rules[i].min_group_qty)) {
      chosen = rules[i];
      break;
    }
  }
  if (!chosen) return null;
  const groupSubtotal = u * q;
  let amount = 0;
  if (chosen.kind === "percent") {
    const pct = Math.max(0, Math.min(100, Number(chosen.value || 0)));
    amount = (groupSubtotal * pct) / 100;
  } else if (chosen.kind === "amount") {
    amount = Math.min(groupSubtotal, Math.max(0, Number(chosen.value || 0)));
  }
  // Round to cents using Math.floor so we never advertise a discount
  // larger than the server will actually apply (mirrors the backend's
  // floor_cents helper).
  amount = Math.floor(amount * 100) / 100;
  if (amount <= 0) return null;
  return { rule: chosen, amount };
}

/**
 * Lowest-threshold rule a teaser badge can advertise on the Shop
 * listing card (where no quantity stepper exists). Returns `null` if
 * no rules are configured.
 */
export function lowestThresholdRule(rules) {
  if (!rules || rules.length === 0) return null;
  return rules[0]; // already sorted asc by min_group_qty server-side
}

/**
 * Compact "off" label for a rule, e.g. "10% off" or "$25 off".
 */
export function formatRuleSavings(rule) {
  if (!rule) return "";
  if (rule.kind === "percent") {
    return `${Number(rule.value || 0)}% off`;
  }
  return `$${Number(rule.value || 0).toFixed(2)} off`;
}
